import {
  Box,
  Chevron,
  Disclaimer,
  Div,
  ErrorToast,
  H5,
  Icon,
  Img,
  isAdmin,
  isPartner,
  P,
  ProviderConnectionStatusComponent,
  ProviderConnectionTypeComponent,
  ProviderStatusComponent,
  renderProviderImage,
  renderProviderName,
  renderProviderPhone,
  SimpleLocationEntity,
  SyncStatus,
  useSafeWindowOpen,
  VerticalSeparator,
} from '@vgrubs/components';
import {
  ProviderAndProviderStatusResponse,
  ProviderConnectionType,
} from 'components/src/types/api/location.types';
import {
  BrandProviderConfigEntity,
  SimpleBrandEntity,
} from 'components/src/types/brand.types';
import {
  Provider,
  ProviderConnect,
  ProviderConnectionStatus,
  ProviderStatus,
} from 'components/src/types/shared.types';
import { useState } from 'react';
import { ClockSolidIcon } from '../../assets/svg';
import { lightTheme } from '../../styles/theme';
import { ConnectionTimingRow } from './ProviderComponent.styled';
import { pausedUntilLabel, scheduledPauseStartLabel } from './providerPause';
import { ScheduledPausesState } from './useScheduledPauses';
import { providerConnectionTiming } from './providerTiming';
import { ProviderConnectModal } from './ProviderConnectModal';
import { ProviderDetailsModal } from './ProviderDetails';
import { SetupMenuToContinueModal } from './SetupMenuToContinueModal';

export const ProviderComponent = ({
  provider,
  id,
  status,
  pathPrefix,
  statuses,
  setStatuses,
  credentials,
  setProviderConnect,
  getProvidersStatuses,
  location,
  brand,
  syncStatus,
  buttonLoading,
  timeZone,
  scheduled,
}: {
  provider: Provider;
  id: string | number;
  status:
    | {
        provider: Provider;
        status: ProviderStatus;
        connectionStatus: ProviderConnectionStatus;
        connectionType: ProviderConnectionType;
        haveMenuSet?: boolean | null | undefined;
        haveWorkingHoursSet?: boolean | null | undefined;
        inProgressConnectAllowed: boolean;
        storeLink: string | null;
        pausedUntil?: string | null;
        id: number;
      }
    | undefined;
  pathPrefix: 'locations' | 'brands';

  setStatuses: (value: ProviderAndProviderStatusResponse) => void;
  statuses: ProviderAndProviderStatusResponse;
  credentials: BrandProviderConfigEntity | undefined;
  setProviderConnect: (provider: ProviderConnect | null) => void;
  getProvidersStatuses: (silent?: boolean) => void;
  location: SimpleLocationEntity | null;
  brand: SimpleBrandEntity | null;
  syncStatus: SyncStatus | null;
  buttonLoading: boolean;
  /** The location's: a pause end is shown on its clock. */
  timeZone?: string;
  scheduled: ScheduledPausesState;
}) => {
  const [providerDetailsModal, setProviderDetailsModal] = useState(false);
  const [providerConnectModal, setProviderConnectModal] = useState(false);
  const [setupMenuToContinueModal, setSetupMenuToContinueModal] =
    useState(false);
  const onlineStatus = status?.status;
  const connectionStatus = status?.connectionStatus;

  // How long the marketplace takes to go live. Only worth saying while it is
  // not live yet; on a connected row it is history.
  const timing =
    connectionStatus === ProviderConnectionStatus.connected
      ? null
      : providerConnectionTiming(provider);
  const connectionType = status?.connectionType;

  const pausedUntil =
    onlineStatus === ProviderStatus.offline
      ? pausedUntilLabel(status?.pausedUntil, timeZone)
      : null;

  const nextScheduled = scheduled.of(provider)[0];
  const scheduledLabel = nextScheduled
    ? `Pause scheduled ${scheduledPauseStartLabel(nextScheduled, timeZone)}`
    : null;

  const handleProviderClick = () => {
    if (isPartner && provider === Provider.kanekt365) {
      ErrorToast('To connect Kanekt 365 please reach out to our support.');
      return;
    }

    if (
      connectionStatus === ProviderConnectionStatus.connected ||
      connectionStatus === ProviderConnectionStatus.waiting ||
      connectionStatus === ProviderConnectionStatus.rejected ||
      connectionStatus === ProviderConnectionStatus.disabled ||
      connectionStatus === ProviderConnectionStatus.waitingMenu ||
      connectionStatus === ProviderConnectionStatus.inProgress
    ) {
      setProviderDetailsModal(true);
    } else {
      setProviderConnectModal(true);
    }
  };

  const openSafe = useSafeWindowOpen();

  const handleOpenStoreLink = (e: React.MouseEvent<HTMLParagraphElement>) => {
    e.stopPropagation();
    e.preventDefault();
    openSafe(status?.storeLink ?? '', {
      immediate: true,
      target: '_blank',
    });
  };

  const ViewStoreLink = () => {
    return (
      <Disclaimer
        $m="0"
        $underline
        $pointer
        $hover
        onClick={handleOpenStoreLink}
      >
        View Online Store Link
      </Disclaimer>
    );
  };

  return (
    <>
      <Box
        $mb="8"
        $flex
        $alignCenter
        $between
        onClick={handleProviderClick}
        $pointer={true}
      >
        <Div $flex $alignCenter>
          <Img
            $width="65px"
            $widthSM="50px"
            src={renderProviderImage(provider)}
            alt="provider"
          />
          <VerticalSeparator $m="0 16px" $mSM="0 12px" $h="50px" />
          <Div>
            <H5 $mb="4">{renderProviderName(provider)}</H5>
            <P $mb="0">{renderProviderPhone(provider)}</P>
            {timing && (
              <ConnectionTimingRow>
                <ClockSolidIcon color={lightTheme.lightGray} />
                <Disclaimer $m="0" $color={lightTheme.lightGray}>
                  {timing.rowLabel}
                </Disclaimer>
              </ConnectionTimingRow>
            )}
            {isAdmin && status?.storeLink && (
              <Div $mobile $mt="4">
                <ViewStoreLink />
              </Div>
            )}
          </Div>
        </Div>
        <Div $flex $alignCenter $gap="20px" $gapSM="12px">
          <Div $flex $column $alignEnd $gap="8px" $gapSM="4px">
            <Div
              $flex
              $alignCenter
              $columnSM
              $alignEndSM
              $gap="12px"
              $gapSM="4px"
            >
              {isAdmin && connectionType && (
                <ProviderConnectionTypeComponent
                  connectionType={connectionType}
                />
              )}

              {onlineStatus && (
                <ProviderStatusComponent
                  status={onlineStatus as ProviderStatus}
                />
              )}
            </Div>

            {pausedUntil && (
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                {pausedUntil}
              </Disclaimer>
            )}

            {scheduledLabel && (
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                {scheduledLabel}
              </Disclaimer>
            )}

            {isAdmin && status?.storeLink && (
              <Div $desktop>
                <ViewStoreLink />
              </Div>
            )}
          </Div>

          {connectionStatus && (
            <ProviderConnectionStatusComponent
              status={connectionStatus as ProviderConnectionStatus}
            />
          )}

          {!onlineStatus && !connectionStatus && (
            <Icon $rotate="90deg">
              <Chevron />
            </Icon>
          )}
        </Div>
      </Box>
      <ProviderDetailsModal
        modalIsOpen={providerDetailsModal}
        closeModal={() => {
          setProviderDetailsModal(false);
        }}
        provider={provider}
        id={id}
        pathPrefix={pathPrefix}
        setStatuses={setStatuses}
        statuses={statuses}
        status={status}
        credentials={credentials}
        getProvidersStatuses={getProvidersStatuses}
        setSetupMenuToContinueModal={setSetupMenuToContinueModal}
        connectionType={connectionType}
        timeZone={timeZone}
        scheduled={scheduled}
      />
      <ProviderConnectModal
        getProvidersStatuses={getProvidersStatuses}
        modalIsOpen={providerConnectModal}
        closeModal={() => {
          setProviderConnectModal(false);
        }}
        provider={provider}
        id={id}
        pathPrefix={pathPrefix}
        setStatuses={setStatuses}
        statuses={statuses}
        setProviderConnect={setProviderConnect}
        setSetupMenuToContinueModal={setSetupMenuToContinueModal}
        location={location}
      />

      <SetupMenuToContinueModal
        syncStatus={syncStatus}
        isOpen={setupMenuToContinueModal}
        closeModal={() => setSetupMenuToContinueModal(false)}
        locationId={pathPrefix === 'locations' ? id : location?.id ?? '0'}
        brandId={pathPrefix === 'brands' ? id : '0'}
        provider={provider}
        haveMenuSet={status?.haveMenuSet}
        haveWorkingHoursSet={status?.haveWorkingHoursSet}
        location={location}
        brand={brand}
        pathPrefix={pathPrefix}
        id={id}
        buttonLoading={buttonLoading}
      />
    </>
  );
};
